import { type ReactElement } from 'react';
import { formatDateRange, getNumberOfGuests } from '@fshr/shared';

import type { Booking } from './types';

export type SelectActiveBookingProps = {
	bookings: Booking[];
	resortName?: string;
	selectedReservationId: string | null;
	onSelectionChange: (reservationId: string) => void;
	t: (text: string, params?: (string | number)[]) => string;
};

type BookingHotelProduct = NonNullable<Booking['hotelProducts']>[number];

function findNumberOfGuests(hotelProduct: BookingHotelProduct | undefined) {
	if (hotelProduct?.guestCount) return hotelProduct.guestCount;

	const rooms = hotelProduct?.rooms?.room ?? [];
	return rooms.reduce(
		(acc, room) => ({
			numberAdults: acc.numberAdults + (room.numAdults || 0),
			numberChildren: acc.numberChildren + (room.numChildren || 0),
		}),
		{ numberAdults: 0, numberChildren: 0 }
	);
}

export default function SelectActiveBooking({
	bookings,
	resortName,
	selectedReservationId,
	onSelectionChange,
	t,
}: SelectActiveBookingProps): ReactElement {
	const sortedBookings = [...bookings].sort(
		(a, b) => new Date(a.startDate as string).getTime() - new Date(b.startDate as string).getTime()
	);

	return (
		<div className="flex flex-col gap-300" data-testid="select-active-booking">
			<span className="text-caption text-text-secondary">
				{t('Please choose an upcoming stay to add this to your itinerary.')}
			</span>

			{resortName && <span className="text-subtitle2 text-text-heading">{resortName}</span>}

			{/* AC 3: list scrolls internally when > 3 stays. max-h ≈ 3 cards × ~89px + 2 gaps × 20px = 307px,
			    rounded up. No token covers this range (custom scale tops at 1000 = 100px). */}
			<div className="flex max-h-[320px] flex-col gap-200 overflow-y-auto">
				{sortedBookings.map(booking => {
					const hotelProduct = booking.hotelProducts?.[0];
					const reservationId = hotelProduct?.reservationId;
					const { numberAdults, numberChildren } = findNumberOfGuests(hotelProduct);
					const isSelected = selectedReservationId === reservationId;

					if (!reservationId) return null;

					return (
						<button
							key={reservationId}
							type="button"
							onClick={() => onSelectionChange(reservationId)}
							data-testid="select-active-booking-card"
							className={`flex flex-col gap-040 rounded-sm border p-200 text-left transition-colors ${
								isSelected
									? 'border-border-strong bg-neutral-100'
									: 'border-border-subtle bg-surface-default hover:bg-neutral-100'
							}`}
						>
							<span className="text-body2 text-text-heading">
								{formatDateRange(booking.startDate as string, booking.endDate as string)}
								{' · '}
								{getNumberOfGuests({
									numberOfAdults: numberAdults,
									numberOfChildren: numberChildren,
									t,
								})}
							</span>
							<span className="text-caption text-text-secondary">
								{t('Confirmation #: {0}', [reservationId])}
							</span>
						</button>
					);
				})}
			</div>
		</div>
	);
}
